
import React, { useState, useEffect } from 'react';
import { WorkItem, GeneratedImage, ReferenceAsset } from './types';
import { parseCSV } from './utils/helpers';
import { matchAssetsToPrompt } from './services/geminiService';
import { usePersistence } from './hooks/usePersistence';
import { useGeneration } from './hooks/useGeneration';
import AssetSidebar from './components/AssetSidebar';
import EditModal from './components/EditModal';
import SettingsModal from './components/SettingsModal';
import ImageViewerModal from './components/ImageViewerModal';
import AssetPickerModal from './components/AssetPickerModal';
import CsvMappingModal from './components/CsvMappingModal';
import { Header } from './components/Header';
import { Toolbar } from './components/Toolbar';
import { WorkItemCard } from './components/WorkItemCard';
import { GalleryView } from './components/GalleryView';
import { Upload } from 'lucide-react';
import { TutorialGuide } from './components/TutorialGuide';

export default function App() {
  const { settings, setSettings, workItems, setWorkItems, assets, setAssets, hasLoaded, resolution, setResolution, aspectRatio, setAspectRatio, batchSize, setBatchSize } = usePersistence();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const { isProcessing, generateForItem, runBatchGeneration } = useGeneration(workItems, setWorkItems, assets, settings, resolution, aspectRatio, batchSize, setIsSettingsOpen);
  const [selectedImageForEdit, setSelectedImageForEdit] = useState<GeneratedImage | null>(null);
  const [viewingImage, setViewingImage] = useState<GeneratedImage | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTargetId, setPickerTargetId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTempText, setEditTempText] = useState("");

  // View mode state
  const [viewMode, setViewMode] = useState<'list' | 'gallery'>('list');

  // CSV Import State
  const [isMappingModalOpen, setIsMappingModalOpen] = useState(false);
  const [pendingCsvData, setPendingCsvData] = useState<any[][]>([]);
  const [pendingHeaders, setPendingHeaders] = useState<string[]>([]);

  // Tutorial State
  const [isTutorialOpen, setIsTutorialOpen] = useState(false);

  // Check for first time user
  useEffect(() => {
    const hasSeenTutorial = localStorage.getItem('genstudio_tutorial_seen');
    if (!hasSeenTutorial) {
      setTimeout(() => setIsTutorialOpen(true), 500);
    }
  }, []);

  const handleCloseTutorial = () => {
    setIsTutorialOpen(false);
    localStorage.setItem('genstudio_tutorial_seen', 'true');
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      try {
        const { data, headers } = await parseCSV(e.target.files[0]);
        if (data.length === 0) {
          alert("CSV 文件为空。");
          return;
        }
        setPendingCsvData(data);
        setPendingHeaders(headers);
        setIsMappingModalOpen(true);
      } catch (err) { alert("CSV 解析错误。请确保文件格式正确。"); }
      e.target.value = '';
    }
  };

  const handleMappingConfirm = async (mappedData: { index: string, prompt: string }[]) => {
    const newItems: WorkItem[] = [];
    for (const row of mappedData) {
      const matchedIds = await matchAssetsToPrompt(row.prompt, assets, settings);
      newItems.push({
        id: Math.random().toString(),
        index: row.index,
        prompt: row.prompt,
        status: 'idle',
        generatedImages: [],
        selectedReferenceIds: matchedIds
      });
    }
    setWorkItems(prev => [...prev, ...newItems]);
  };

  const handlePromptSave = async () => {
    if (editingId) {
      const matchedIds = await matchAssetsToPrompt(editTempText, assets, settings);
      setWorkItems(prev => prev.map(item => item.id === editingId ? { ...item, prompt: editTempText, selectedReferenceIds: matchedIds } : item));
      setEditingId(null);
    }
  };

  const handleAssetSelection = (assetIds: string[]) => {
    if (pickerTargetId) setWorkItems(prev => prev.map(item => item.id === pickerTargetId ? { ...item, selectedReferenceIds: assetIds } : item));
  };

  const runMatchAndMerge = async (assetsToCheck: ReferenceAsset[]) => {
    if (assetsToCheck.length === 0 || workItems.length === 0) return;

    const updates = await Promise.all(workItems.map(async (item) => {
      const matchedIds = await matchAssetsToPrompt(item.prompt, assetsToCheck, settings);
      if (matchedIds.length > 0) {
        return { itemId: item.id, newIds: matchedIds };
      }
      return null;
    }));

    const validUpdates = updates.filter(u => u !== null) as { itemId: string, newIds: string[] }[];

    if (validUpdates.length > 0) {
      setWorkItems(prev => prev.map(item => {
        const update = validUpdates.find(u => u.itemId === item.id);
        if (update) {
          return {
            ...item,
            selectedReferenceIds: Array.from(new Set([...item.selectedReferenceIds, ...update.newIds]))
          };
        }
        return item;
      }));
    }
  };

  const handleAssetCreate = (newAssets: ReferenceAsset[]) => {
    setAssets(prev => [...prev, ...newAssets]);
    runMatchAndMerge(newAssets);
  };

  const handleAssetUpdate = (updatedAsset: ReferenceAsset) => {
    setAssets(prev => prev.map(a => a.id === updatedAsset.id ? updatedAsset : a));
    runMatchAndMerge([updatedAsset]);
  };

  const handleAssetDelete = (assetId: string) => {
    setAssets(prev => prev.filter(a => a.id !== assetId));
    setWorkItems(prev => prev.map(item => ({
      ...item,
      selectedReferenceIds: item.selectedReferenceIds.filter(id => id !== assetId)
    })));
  };

  if (!hasLoaded) return <div className="flex h-screen w-full bg-slate-950 items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full"></div></div>;

  return (
    <div className="flex h-screen w-full bg-slate-950 text-slate-200 font-sans overflow-hidden">
      <AssetSidebar
        assets={assets}
        settings={settings}
        onAssetCreate={handleAssetCreate}
        onAssetUpdate={handleAssetUpdate}
        onAssetDelete={handleAssetDelete}
      />
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        <Header
          settings={settings}
          setIsSettingsOpen={setIsSettingsOpen}
          batchSize={batchSize}
          setBatchSize={setBatchSize}
          resolution={resolution}
          setResolution={setResolution}
          aspectRatio={aspectRatio}
          setAspectRatio={setAspectRatio}
          workItems={workItems}
          onOpenTutorial={() => setIsTutorialOpen(true)}
        />
        <Toolbar
          onFileUpload={handleFileUpload}
          workItemsCount={workItems.length}
          onClearAll={() => { if (window.confirm("确定要清空所有任务和图片吗？")) setWorkItems([]); }}
          isProcessing={isProcessing}
          onRunBatch={runBatchGeneration}
          viewMode={viewMode}
          setViewMode={setViewMode}
        />

        <div className="flex-1 overflow-y-auto bg-slate-950">
          {viewMode === 'list' ? (
            <div className="p-4 space-y-3">
              {workItems.length === 0 && (
                <div className="flex flex-col items-center justify-center py-40 text-slate-600">
                  <Upload className="w-16 h-16 mb-4 opacity-50" />
                  <p className="text-xl font-medium">暂无提示词</p>
                  <p className="text-sm">请导入包含 index 和 prompt 列的 CSV 文件</p>
                </div>
              )}
              {workItems.map((item) => (
                <WorkItemCard key={item.id} item={item} assets={assets} editingId={editingId} editTempText={editTempText} onEditStart={(id, text) => { setEditingId(id); setEditTempText(text); }} onEditChange={setEditTempText} onEditSave={handlePromptSave} onAssetRemove={(itemId, assetId) => setWorkItems(prev => prev.map(i => i.id === itemId ? { ...i, selectedReferenceIds: i.selectedReferenceIds.filter(id => id !== assetId) } : i))} onAssetAdd={(id) => { setPickerTargetId(id); setPickerOpen(true); }} onRegenerate={(id) => generateForItem(id, false)} onViewImage={setViewingImage} onEditImage={setSelectedImageForEdit} />
              ))}
            </div>
          ) : (
            <GalleryView
              workItems={workItems}
              onViewImage={setViewingImage}
              onEditImage={setSelectedImageForEdit}
            />
          )}
        </div>
      </div>
      <SettingsModal isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} settings={settings} onSave={(s) => { setSettings(s); localStorage.setItem('appSettings', JSON.stringify(s)); }} />
      {selectedImageForEdit && <EditModal image={selectedImageForEdit} isOpen={true} onClose={() => setSelectedImageForEdit(null)} onSave={(newImg) => { setWorkItems(prev => prev.map(item => item.prompt === newImg.promptUsed ? { ...item, generatedImages: [newImg, ...item.generatedImages] } : item)); setSelectedImageForEdit(null); }} />}
      {viewingImage && <ImageViewerModal image={viewingImage} isOpen={true} onClose={() => setViewingImage(null)} />}
      <AssetPickerModal isOpen={pickerOpen} onClose={() => setPickerOpen(false)} assets={assets} selectedIds={pickerTargetId ? workItems.find(i => i.id === pickerTargetId)?.selectedReferenceIds || [] : []} onConfirm={handleAssetSelection} />

      <CsvMappingModal
        isOpen={isMappingModalOpen}
        onClose={() => setIsMappingModalOpen(false)}
        rawData={pendingCsvData}
        headers={pendingHeaders}
        onConfirm={handleMappingConfirm}
      />

      <TutorialGuide isOpen={isTutorialOpen} onClose={handleCloseTutorial} />
    </div>
  );
}
